import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  Image,
  Flex,
} from "@chakra-ui/react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { useRef } from "react";
import visionIcon from "../../img/Colored Icon@2x.png";
import missionIcon from "../../img/Colored Icon@2x.png";
import valuesIcon from "../../img/Colored Icon@2x.png";
import whyIcon from "../../img/Colored Icon@2x.png";
import { useBreakpointValue } from "@chakra-ui/react";

const MotionBox = motion(Box);
const MotionHeading = motion(Heading);

export default function VisionMission() {
  const { i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const accentColor = "#f6b800"; 
  const textColor = "#000";

  const sections = [
    {
      title: isArabic ? "الرؤية" : "Vision",
      description: isArabic
        ? "نقود صناعة المحتوى الرقمي بطابع محلي بروح رؤية تلهم العالم"
        : "To achieve global leadership in the film and creative content industry, setting new standards for innovation, quality, and cultural impact",
      icon: visionIcon,
    },
    {
      title: isArabic ? "الرسالة" : "Mission",
      description: isArabic
        ? "نركز على وضوح الفكرة لتقديم المحتوى البصري الذي يحقق الأثر"
        : "Colors bring elegance to creative work and that’s where our story begins. We craft visual narratives that inspire, inform, and captivate",
      icon: missionIcon,
    },
    {
      title: isArabic ? "لماذا الفا كرييتف " : "Why Alpha Creative",
      description: isArabic
        ? "نقدّم حلولًا تجمع بين الإبداع، الجودة، والتميز في كل التفاصيل"
        : "We blend creativity, quality, and innovation in every project",
      icon: whyIcon,
    },
    {
      title: isArabic ? "ما الذي يميزنا" : "What Sets Us Apart",
      description: isArabic
        ? "نستقطب أفضل المواهب ونبني شراكات استراتيجية لضمان الابتكار المستمر"
        : "We attract top talent and forge partnerships to ensure constant innovation",
      icon: valuesIcon,
    },
  ];

  return (
    <Box
      id="vision-mission"
      bg={"#f2be44"}
position="relative"
_before={{
  content: '""',
  position: "absolute",
  inset: 0,
  bg: "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.25), transparent 70%)",
  opacity: 0.5,
}}
      color={textColor}
      py={{ base: 16, md: 24, lg: 28 }}
      px={{ base: 4, md: 10 }}
      dir={isArabic ? "rtl" : "ltr"}
   
      overflow="hidden"
      w="100%"
    >
      <motion.div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at 40% 20%, rgba(255,255,255,0.3), transparent 70%)",
          zIndex: 0,
        }}
        animate={{
          backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"],
        }}
        transition={{ duration: 14, ease: "linear", repeat: Infinity }}
      />

    
      <Box
        position="absolute"
        inset={0}
        backgroundImage={`url(${visionIcon})`}
        backgroundRepeat="no-repeat"
        backgroundSize="70%"
        backgroundPosition="center"
        opacity="0.06"
        filter="blur(3px)"
        zIndex={0}
      />

      <Container maxW="7xl" position="relative" zIndex={1}>
      <MotionHeading
  textAlign={isArabic ? "right" : "left"}
  fontSize={{ base: "2xl", md: "4xl", lg: "5xl" }}
  mb={14}
  fontWeight="bold"
  letterSpacing="-0.02em"
  lineHeight="1.3"
  initial={{ opacity: 0, y: 60 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ duration: 1, ease: [0.33, 1, 0.68, 1] }}
  viewport={{ once: true }}
>
  {isArabic ? (
    "رؤية تُلهم، ورسالة تُبدع"
  ) : (
    <>
      <Box display={{ base: "block", md: "inline" }}>
        A Vision That Inspires,
      </Box>
      <Box display={{ base: "block", md: "inline" }}>
        {" "}
        A Mission That Creates
      </Box>
    </>
  )}
</MotionHeading>


        <SimpleGrid
          columns={{ base: 1, md: 2 }}
          spacing={{ base: 10, md: 12, lg: 16 }}
        >
          {sections.map((section, index) => (
            <FloatingCard
              key={index}
              section={section}
              isArabic={isArabic}
              accentColor={accentColor}
              delay={index * 0.1}
            />
          ))}
        </SimpleGrid>
      </Container>
    </Box>
  );
}

function FloatingCard({ section, isArabic, accentColor, delay }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-50, 50], [6, -6]);
  const rotateY = useTransform(x, [-50, 50], [-6, 6]);

  const isDesktop = useBreakpointValue({ base: false, lg: true }); 

  const handleMouseMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const xVal = e.clientX - rect.left - rect.width / 2;
    const yVal = e.clientY - rect.top - rect.height / 2;
    x.set(xVal / 5);
    y.set(yVal / 5);
  };
  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  const formattedDescription = (() => {
    if (isArabic || !isDesktop) return section.description;

    let desc = section.description;

    if (section.title === "Vision" || section.title === "Mission") {
      const parts = desc.split(" and ");
      if (parts.length > 2) {
        desc = `${parts[0]} and ${parts[1]}<br />and ${parts.slice(2).join(" and ")}`;
      }
    }

    return desc;
  })();


  return (
    <MotionBox
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ rotateX, rotateY }}
      transformPerspective={900}
      p={{ base: 6, md: 8 }}
      bg="rgba(255,255,255,0.25)"
      border="1px solid rgba(255,255,255,0.35)"
      color="#000"
      borderRadius="2xl"
      backdropFilter="blur(10px)"
      boxShadow="0 10px 25px rgba(0,0,0,0.15)"
      textAlign={isArabic ? "right" : "left"}
      alignItems={isArabic ? "flex-end" : "flex-start"}
      display="flex"
      flexDirection="column"
      transition="all 0.4s ease"
      initial={{ opacity: 0, y: 60, scale: 0.95 }}
      whileInView={{
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 1, delay, ease: [0.33, 1, 0.68, 1] },
      }}
      whileHover={{
        scale: 1.02,
      }}
    >
      <Flex align="center" gap={4} mb={4} w="100%">
        <Image
          src={section.icon}
          alt={`${section.title} icon`}
          boxSize={{ base: "40px", md: "50px", lg: "55px" }}
          objectFit="contain"
        />
        <Heading fontSize={{ base: "xl", md: "2xl" }} fontWeight="semibold">
          {section.title}
        </Heading>
      </Flex>

      <Text
        fontSize={{ base: "md", md: "lg" }}
        lineHeight="1.9"
        textAlign={isArabic ? "right" : "left"}
        w="100%"
        dangerouslySetInnerHTML={{ __html: formattedDescription }}
      />
    </MotionBox>
  );
}

